import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { Permission } from '@employer/app/config/permission.config'
import { ManageCompanyLogoComponent } from '@employer/app/modules/customer/views/manage-company-logo/manage-company-logo.component'
import { ManageEmployerSettingsComponent } from '@employer/app/modules/customer/views/manage-employer-settings/manage-employer.component'
import { featureOnCanMatch } from '@employer/app/services/feature-flag.guard'
import { NavItemNames } from '@employer/app/services/navigation.service'
import { PermissionsGuard } from '@engineering11/access-web'
import { FeatureGuard } from '@engineering11/config-web'
import { CnectFeature, JobAutomationPermissions } from 'shared-lib'
import { FileNotFoundComponent } from '../_404/404.component'
import { InterviewQuestionsListComponent } from '../interview-question-library/components/interview-question-list/interview-question-list.component'
import { employerGuard } from './employer.guard'
import { CareersPageHeroComponent } from './views/careers-page-hero/careers-page-hero.component'
import { CustomerComplianceComponent } from './views/compliance.component'
import { CustomerDepartmentsComponent } from './views/departments.component'
import { CustomerHomeComponent } from './views/home/customer-home.component'
import { JobApplicationSettingsComponent } from './views/job-application-settings/job-application-settings.component'
import { JobsPostAutomationCompanySettingsComponent } from './views/jobs-post-automation-company-settings/jobs-post-automation-company-settings.component'
import { CustomerLocationsComponent } from './views/locations.component'
import { CustomerSocialMediaComponent } from './views/social-media/social-media.component'

const employerInterviewQuestions: CnectFeature = 'employerInterviewQuestions'
const employerJobAutomation: CnectFeature = 'employerJobAutomation'

// Title Information
// In the router the 'title' is for browser's title shown in the browser tab
// The 'data title' is used by the view to update the title service for the page's header

const routes: Routes = [
  {
    path: '',
    canActivate: [PermissionsGuard, employerGuard],
    data: { permission: Permission.ManageCompanySettings },
    children: [
      // CNCT-6779: these are children of /settings now, so the empty path belongs to the
      // hub landing one level up — nothing here claims it.
      {
        path: 'company-account',
        component: CustomerHomeComponent,
        data: { title: 'Company Account Settings' },
      },
      {
        path: 'logo',
        component: ManageCompanyLogoComponent,
        data: { title: 'Company Logo and Video' },
      },
      {
        path: 'content',
        component: ManageEmployerSettingsComponent,
        data: { title: 'Site Content' },
      },
      {
        path: 'departments',
        component: CustomerDepartmentsComponent,
        data: { title: 'Company Departments' },
      },
      {
        path: 'departments/detail',
        component: CustomerDepartmentsComponent,
        data: { title: 'Company Department Detail' },
      },
      {
        path: 'locations',
        component: CustomerLocationsComponent,
        data: { title: 'Company Locations' },
      },
      {
        path: 'locations/detail',
        component: CustomerLocationsComponent,
        data: { title: 'Company Location Detail' },
      },
      {
        path: 'hero',
        component: CareersPageHeroComponent,
        data: { title: 'Hero Section' },
      },
      {
        path: 'social',
        component: CustomerSocialMediaComponent,
        data: { title: 'Social Media' },
      },

      {
        canActivate: [FeatureGuard],
        path: 'interview-questions',
        component: InterviewQuestionsListComponent,
        data: { feature: employerInterviewQuestions, title: 'Interview Questions' },
      },
      {
        path: 'compliance',
        component: CustomerComplianceComponent,
        data: { title: 'Compliance' },
      },
      {
        path: 'automation-settings',
        component: JobsPostAutomationCompanySettingsComponent,
        canActivate: [PermissionsGuard, FeatureGuard],
        data: {
          title: 'Automation Settings',
          feature: employerJobAutomation,
          permission: [JobAutomationPermissions.ManageAutomation],
        },
      },
      // Story 2.2a: gated by `employerJobApplicationSettings`. Defaults OFF in
      // production until the existing component is verified working. Flag OFF →
      // canMatch fails and the navigation falls through to the 404 redirect below,
      // so a disabled feature shows no stub on a deep-link (nav item is hidden too).
      {
        path: 'job-application-settings',
        canMatch: [featureOnCanMatch('employerJobApplicationSettings')],
        component: JobApplicationSettingsComponent,
        canActivate: [PermissionsGuard],
        data: {
          title: 'Opening & Application Settings',
        },
      },
      { path: 'job-application-settings', redirectTo: '/jobs/404', pathMatch: 'full' },
      {
        path: '**',
        component: FileNotFoundComponent,
        data: { title: NavItemNames.FileNotFound },
      },
    ],
  },
]

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CustomerRoutingModule {}
